import React, { useEffect, useState } from 'react';
import { Drawer } from 'antd';
import { Resizable } from 'react-resizable';
import Popup from '../../components/Popup/Popup';
import { IBook, TColumns } from '../../section/Common/types/types';
import onContextMenu from './onContextMenu';
import {
  ALL_EXCLUDED,
  AUTHORS,
  BOOKSTAT_DATA,
  PODIUMAUTHORS,
  PODIUM_SALES,
  PODIUM_TITLES,
  SOURCE,
  TOP_POD_TITLES
} from '../../core/constants';
import './styles.css';
import SendAll from '../Actions/SendAll';
import BulkExclude from '../Actions/Exclude/BulkExclude';
import DeleteAllTitles from '../Actions/Delete/DeleteAllTitles';
import BlockSelectedAuthors from '../Actions/BlockAuthor/BlockSelectedAuthors';
import MuteSelectedAuthors from '../Actions/MuteAuthor/MuteSelectedAuthors';
import UnmuteSelectedAuthors from '../Actions/UnmuteAuthor/UnmuteSelectedAuthors';
import PinUnpinSelectedTitles from '../Actions/PinTitles/PinUnpinSelectedTitles';
import Table from '../../components/Table';
import { useUserPermit } from '../../context/User.context';
import { useActions } from '../../context/Actions.context';
import OverlayContent from '../Overlay/Overlay';
import NotesOverlay from '../NotesOverlay/NotesOverlay';
import { tabOptionsMapping } from './tabOptions';
import { useFilter } from '../../context/Filter.context';
import { DailyRankTabs } from '../../section/Common/constants';

interface IPopup {
  record: Object;
  visible: boolean;
  x: number;
  y: number;
}

interface IProps {
  fontSize: string;
  dataSource: IBook[] | undefined;
  isFetching: boolean;
  columns: TColumns[];
  id: string;
  refetchTable: Function;
}

const ResizeableTitle = ({
  onResize,
  onResizeStop,
  onResizeStart,
  width,
  ...restProps
}) => {
  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      onResize={onResize}
      onResizeStart={onResizeStart}
      onResizeStop={onResizeStop}
    >
      <th key='a' {...restProps} />
    </Resizable>
  );
};

const ResizeTable: React.FC<IProps> = ({
  fontSize,
  id,
  dataSource,
  isFetching,
  columns,
  refetchTable
}) => {
  const [resize, setResize] = useState<any>({ isResizing: false, columns });
  const [visibleContextMenu, setVisibleContextMenu] = useState(false);
  const { edit_titles } = useUserPermit();
  const { source: sourcePath, isActionLoading } = useActions();
  const noMultiTitleOptionTabs = [BOOKSTAT_DATA, PODIUM_SALES, PODIUMAUTHORS, AUTHORS, TOP_POD_TITLES];
  const tabsWithNoOverlay = new Set([
    BOOKSTAT_DATA,
    PODIUM_SALES,
    AUTHORS,
    PODIUMAUTHORS,
    TOP_POD_TITLES,
    ...DailyRankTabs
  ]);

  const { filter } = useFilter();

  const [popup, setPopup] = useState<IPopup>({
    record: {},
    visible: false,
    x: 0,
    y: 0
  });

  const [openOverlay, setOpenOverlay] = useState({
    book: {},
    visible: false
  });

  // Notes overlay
  const [notesOverlay, setNotesOverlay] = useState<{
    book: Record<string, any>;
    visible: boolean;
  }>({
    book: {},
    visible: false
  });

  const handleOpenNotes = (book: Record<string, any>) => {
    setNotesOverlay({ visible: true, book });
  };

  const handleCloseNotes = () => {
    setNotesOverlay({ visible: false, book: {} });
  };

  useEffect(() => {
    setResize(({ isResizing }) => ({ isResizing, columns }));
  }, [columns]);

  useEffect(() => {
    const handleOpenNotesEvent = (e: any) => {
      handleOpenNotes(e.detail);
    };
    window.addEventListener('open-notes-overlay', handleOpenNotesEvent);
    return () => window.removeEventListener('open-notes-overlay', handleOpenNotesEvent);
  }, []);

  const components = { header: { cell: ResizeableTitle } };

  const handleResize = index => (e, obj) => {
    const nextColumns = [...resize.columns];

    nextColumns[index] = {
      ...nextColumns[index],
      width: obj.size.width
    };

    setResize({ isResizing: false, columns: nextColumns });
  };

  const onResizeStart = () => {
    const col = resize.columns;
    setResize({ ...resize, columns: col });
  };

  const onResizeStop = () => {
    const col = resize.columns;
    setResize({ ...resize, columns: col });
  };

  const columnsT = resize?.columns.map((col, index) => ({
    ...col,
    onHeaderCell: column => ({
      width: column.width,
      onResize: handleResize(index),
      onResizeStart,
      onResizeStop
    })
  }));

  const handleRowClick = (book) => {
    setOpenOverlay({ visible: true, book });
  };

  const handleCancel = () => {
    setOpenOverlay({ visible: false, book: {} });
  };

  useEffect(() => {
    if (!sourcePath['source']?.includes('daily-rank')) {
      const th = document.createElement('th') as HTMLElement;
      th.classList.add('ant-table-cell', 'cell-fix', 'ant-table-cell-fix-right');
      const tr = document.querySelector('tr');
      tr?.appendChild(th);
    }

    if (dataSource && dataSource?.length === 1 && window.location.search) {
      handleRowClick(dataSource[0]);
    }

    return () => {
      handleCancel();
    };
  }, []);

  useEffect(() => {
    if (isActionLoading) {
      handleCancel();
    }
  }, [isActionLoading]);

  const [openConfirm, setOpenConfirm] = useState({ type: '' });
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openBlockAuthorsModal, setOpenBlockAuthorsModal] = useState<boolean>(false);
  const [openMuteAuthorsModal, setOpenMuteAuthorsModal] = useState<boolean>(false);
  const [openUnmuteAuthorsModal, setOpenUnmuteAuthorsModal] = useState<boolean>(false);
  const [openPinUnpinModal, setOpenPinUnpinModal] = useState<'pin' | 'unpin' | ''>('');
  const [selectedTitlesKeys, setSelectedTitlesKeys] = useState<any>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  const tabOptions = tabOptionsMapping({
    selectedTitlesKeys,
    selectedRows,
    setOpenConfirm,
    setOpenDeleteModal,
    setOpenBlockAuthorsModal,
    setOpenMuteAuthorsModal,
    setOpenUnmuteAuthorsModal,
    setOpenPinUnpinModal
  });

  const rowSelection: any = {
    onChange: (selectedRowKeys: [], selectedRowsData: any[]) => {
      setSelectedTitlesKeys(selectedRowKeys);
      setSelectedRows(selectedRowsData);
    },
    selections: tabOptions[id] || []
  };

  // Bulk exclusion uses the rich BulkExclude modal for Bookstat only.
  // Royal Road & AO3 keep the existing simple confirmation (SendAll).
  const isBulkExcludeAction =
    openConfirm?.type === ALL_EXCLUDED && sourcePath['source'] === SOURCE.bookstat;

  const handleClose = () => {
    setOpenConfirm({ type: '' });
  };

  const handleReset = () => {
    setSelectedTitlesKeys([]);
    setSelectedRows([]);
  };

  const selections = sourcePath['source'] !== SOURCE['daily-rank'] &&
    !noMultiTitleOptionTabs.includes(id) && edit_titles && {
    type: 'checkbox',
    selectedRowKeys: selectedTitlesKeys,
    ...rowSelection
  };

  return (
    <>
      <DeleteAllTitles
        book_ids={selectedTitlesKeys}
        open={openDeleteModal}
        onReset={() => handleReset()}
        onClose={() => setOpenDeleteModal(false)}
        id={id}
      />
      <BlockSelectedAuthors
        selected_ids={selectedTitlesKeys}
        open={openBlockAuthorsModal}
        onReset={() => handleReset()}
        onClose={() => setOpenBlockAuthorsModal(false)}
        id={id}
      />
      <MuteSelectedAuthors
        selected_authors={selectedRows.map(row => row.Author)}
        open={openMuteAuthorsModal}
        onReset={() => handleReset()}
        onClose={() => setOpenMuteAuthorsModal(false)}
        id={id}
      />
      <UnmuteSelectedAuthors
        selected_authors={selectedRows.map(row => row.Author)}
        open={openUnmuteAuthorsModal}
        onReset={() => handleReset()}
        onClose={() => setOpenUnmuteAuthorsModal(false)}
        id={id}
      />
      <PinUnpinSelectedTitles
        book_ids={selectedTitlesKeys}
        open={openPinUnpinModal !== ''}
        isPinning={openPinUnpinModal === 'pin'}
        onReset={() => handleReset()}
        onClose={() => setOpenPinUnpinModal('')}
        id={id}
      />
      <SendAll
        book_ids={selectedTitlesKeys}
        open={Boolean(openConfirm?.type) && !isBulkExcludeAction}
        name={openConfirm?.type}
        onClose={handleClose}
        onReset={handleReset}
        id={id}
      />
      <BulkExclude
        selectedRows={selectedRows}
        open={isBulkExcludeAction}
        onClose={handleClose}
        onReset={handleReset}
        id={id}
      />

      <Table
        pagination={false}
        components={components}
        columns={columnsT}
        dataSource={dataSource}
        style={{ fontSize }}
        bordered
        rowSelection={selections}
        loading={isFetching}
        onRow={record => {
          const isPodiumTitle = id === PODIUM_TITLES;

          if (tabsWithNoOverlay.has(id)) {
            return {};
          }

          const rowClassName = sourcePath['source'] !== SOURCE['daily-rank'] ? 'cursor-pointer' : '';

          const commonProps = {
            onClick: () => {
              if (!notesOverlay.visible) {
                handleRowClick(record);
              }
            },
            className: `
              ${rowClassName}
              ${record?.is_pinned_watchlist ? 'watchlist-pinned-row' : ''}
            `
          };

          if (isPodiumTitle) {
            return {
              ...commonProps,
              onContextMenu: (e: React.MouseEvent<HTMLElement, MouseEvent>) =>
                onContextMenu(record, e, {
                  visibleContextMenu,
                  setPopup,
                  setVisibleContextMenu
                })
            };
          }

          return commonProps;
        }}
      />

      <Popup {...popup} />

      {/* Book-details overlay */}
      {!tabsWithNoOverlay.has(id) &&
        <Drawer
          visible={openOverlay.visible && !filter[id]?.createDeal}
          onClose={handleCancel}
          destroyOnClose
          placement='right'
          width={'50vw'}
          className='custom-drawer'
        >
          <OverlayContent
            tabId={id}
            book={openOverlay.book}
            refetchTable={refetchTable}
          />
        </Drawer>
      }

      {/* Notes overlay */}
      <NotesOverlay
        visible={notesOverlay.visible}
        onClose={handleCloseNotes}
        book={notesOverlay.book}
      />
    </>
  );
};

export default React.memo(ResizeTable);